/**
 * Quantity-tier price resolver — frontend mirror of backend
 * server.py::resolve_tier_price. Highest-min_qty that the buyer qualifies
 * for wins. Backend is the source of truth at order creation; this is
 * purely display so the customer sees the correct unit price as they
 * adjust quantity in the cart.
 *
 * Sale guard: SKUs flagged ``on_sale`` skip the tier ladder entirely —
 * the headline sale price is canonical during the promo window and
 * stacking it with stale bulk tiers produces inverted/confusing prices.
 * Mirrors backend behaviour so cart totals stay consistent.
 *
 * Safety net: the resolved tier price is capped at product.price so a
 * promotional single-vial price (e.g. BPC-157 £22.99 sale) never
 * presents as MORE expensive at higher quantities than at qty=1.
 */
export function resolveTierPrice(product, qty) {
  if (!product) return 0;
  const base = Number(product.price || 0);
  if (product.on_sale) return base;
  const tiers = product.price_tiers || [];
  if (tiers.length) {
    const sorted = [...tiers].sort((a, b) => Number(b.min_qty) - Number(a.min_qty));
    for (const t of sorted) {
      if (qty >= Number(t.min_qty)) return Math.min(Number(t.unit_price), base);
    }
  }
  return base;
}

export function bestTierPrice(product) {
  /** The lowest unit price across all tiers — used to show "From £X". */
  if (product?.on_sale) return Number(product?.price || 0);
  const tiers = product?.price_tiers || [];
  if (!tiers.length) return Number(product?.price || 0);
  const minTier = tiers.reduce((m, t) => Math.min(m, Number(t.unit_price)), Infinity);
  return Math.min(Number(product.price || Infinity), minTier);
}
